// THE FILE TO EDIT.
//
// Everything the dev team changes for a release lives here: whether the
// feature runs at all, and what the current announcement says. No logic - so
// editing it cannot break the banner, only change what it says.

/**
 * Master switch for the whole feature. Set it to false and nothing renders and
 * nothing is tracked, whatever ACTIVE_ANNOUNCEMENT below says - one line, on
 * Vince's word, without unpicking the copy or touching the component.
 *
 * Two switches rather than one because they answer different questions.
 * `ACTIVE_ANNOUNCEMENT = null` is the ordinary quiet state between releases:
 * we have nothing to say this week. This one means "stop doing this at all" -
 * it survives someone later adding a new announcement without knowing the
 * feature was meant to be off.
 *
 * It is a code switch, so flipping it needs a deploy. Turning it off WITHOUT
 * one means keeping the answer server-side (an account flag, or a settings
 * row a staff screen can write), which is a backend change.
 */
export const ANNOUNCEMENTS_ENABLED = true;

/** The dialog half of an announcement: the centred pop-up. */
export interface AnnouncementDialogContent {
  title: string;
  /** A sentence or two, in a person's voice. */
  body: string;
  /**
   * The control for the thing being announced, shown inline so nobody has to
   * be sent somewhere to try it. "theme" is the light/dark picker.
   */
  inlineControl?: "theme";
  /**
   * Ask them something while we have their attention. `prompt` is stored with
   * every answer, so a later round asking something else stays comparable
   * instead of being averaged in with this one.
   */
  feedback?: {
    intro: string;
    prompt: string;
    submitLabel?: string;
  };
  /**
   * Open it by itself the first time this member lands in the app, rather than
   * waiting for them to click the banner. Shown once; closing it is final, and
   * the banner reopens it for anyone who wants another look.
   */
  autoOpen?: boolean;
}

/**
 * A release announcement, shown in the strip above the navbar until the member
 * closes it, and optionally as a centred dialog.
 *
 * There is one active announcement at a time and it lives in code, so changing
 * it is a deploy. That is deliberate for v1 - the release loop fires when we
 * ship, and shipping is already a deploy.
 */
export interface FeatureAnnouncement {
  /**
   * Identity of the ANNOUNCEMENT, not of the feature. Dismissals are stored
   * against it, so editing the copy of a live announcement leaves it closed
   * for everyone who has already closed it, while a new id shows it to
   * everybody again. Change it only when you mean to re-interrupt people.
   */
  id: string;
  /** One sentence. It shares a 50px strip with the CTA - keep it short. */
  message: string;
  ctaLabel: string;
  /** An in-app route ("/settings/profile") or an absolute https:// link. */
  ctaHref?: string;
  /** Present: the banner's CTA opens this instead of following a link. */
  dialog?: AnnouncementDialogContent;
  /**
   * Who it is for.
   *
   * "paying" leaves out trials, expired trials and cancelled accounts. Someone
   * still deciding whether to buy is being asked to evaluate us, and an
   * interruption about a feature they have not asked for spends their
   * attention on our news rather than their evaluation - and their answer to
   * "what should we build next?" is a guess about a product they have used for
   * a week. Default is everyone.
   */
  audience?: "everyone" | "paying";
  /** ISO date (YYYY-MM-DD). After it neither the banner nor the dialog shows. */
  until?: string;
}

/**
 * The announcement currently running. Set to `null` to show nothing - that is
 * the normal state between releases, and the banner costs nothing while it is
 * null.
 */
export const ACTIVE_ANNOUNCEMENT: FeatureAnnouncement | null = {
  id: "dark-mode-2026-09",
  message: "New: Encharge Dark Mode is here.",
  ctaLabel: "Take a look",
  dialog: {
    title: "Encharge Dark Mode",
    body:
      "We are happy to announce that you can now switch the app to Dark Mode, " +
      "choosing from 10 different color schemes, from near-black to softer " +
      "daytime tones.",
    inlineControl: "theme",
    feedback: {
      intro:
        "We're really ramping up development now, and would genuinely love to " +
        "hear what features you would like us to build.",
      prompt: "What do you think we should build next?",
      submitLabel: "Send it to us",
    },
    // Off on Vince's call after seeing it on staging: this one waits for
    // "Take a look". Left here as an explicit false rather than deleted, so
    // the next release makes the choice on purpose instead of never learning
    // the option exists.
    autoOpen: false,
  },
  audience: "paying",
  until: "2026-10-31",
};
